/**
 * <s-product-image-zoom> — PDP full-screen photo viewer.
 *
 * Gallery buttons open one native dialog declaratively via commandfor/show-modal.
 * This component:
 *  - remembers which photo was clicked and opens the viewer on it,
 *  - moves between photos with the arrow buttons, keyboard ← → and swipe (scroll-snap track),
 *  - keeps the "2 / 5" counter in sync,
 *  - warms the clicked photo on hover/focus so it is ready when the viewer opens.
 *
 * Required markup:
 *   [data-zoom-preload][data-zoom-index][commandfor] — gallery trigger button.
 *   dialog[data-zoom-dialog] [data-zoom-track] > [data-zoom-slide] img[data-zoom-image]
 *   Optional: [data-zoom-prev], [data-zoom-next], [data-zoom-current].
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #dialog = null;
  #track = null;
  #startIndex = 0;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#dialog = this.querySelector('dialog[data-zoom-dialog]');
    this.#track = this.#dialog?.querySelector('[data-zoom-track]') ?? null;

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    // Capture runs before the button's show-modal command opens the dialog.
    this.addEventListener('click', this.#handleTriggerClick, { ...opts, capture: true });

    if (!this.#dialog || !this.#track) return;

    this.#dialog.addEventListener('toggle', this.#handleToggle, opts);
    this.#dialog.addEventListener('keydown', this.#handleKeydown, opts);
    this.#dialog.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.#step(-1), opts);
    this.#dialog.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.#step(1), opts);
    this.#track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    for (const link of this.#preloadLinks) link.remove();
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  get #slides() {
    return this.#track ? [...this.#track.querySelectorAll('[data-zoom-slide]')] : [];
  }

  get #currentIndex() {
    if (!this.#track) return 0;
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  #handleTriggerClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-zoom-index]');
    if (!trigger || !this.contains(trigger)) return;
    this.#startIndex = Number(trigger.getAttribute('data-zoom-index')) || 0;
  };

  #handleToggle = (event) => {
    if (event.newState !== 'open') return;
    this.#goTo(this.#startIndex, 'instant');
  };

  #handleKeydown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(document.dir === 'rtl' ? -1 : 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(document.dir === 'rtl' ? 1 : -1);
    }
  };

  #handleScroll = () => {
    this.#updateCounter();
  };

  #step(direction) {
    const total = this.#slides.length;
    if (total < 2) return;
    // Loop around at either end, like the product gallery.
    const next = (this.#currentIndex + direction + total) % total;
    this.#goTo(next, 'smooth');
  }

  #goTo(index, behavior) {
    const slide = this.#slides[index];
    if (!slide || !this.#track) return;
    const image = slide.querySelector('img');
    if (image instanceof HTMLImageElement) image.loading = 'eager';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({
      left: slide.offsetLeft,
      behavior: reduceMotion ? 'instant' : behavior,
    });
    this.#updateCounter(index);
  }

  #updateCounter(index = this.#currentIndex) {
    const counter = this.#dialog?.querySelector('[data-zoom-current]');
    if (counter) counter.textContent = String(index + 1);
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;
    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;
    this.#preload(Number(source.getAttribute('data-zoom-index')) || 0);
  };

  #preload(index) {
    if (this.#preloaded.has(index)) return;
    const image = this.#slides[index]?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(index);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') image.decode().catch(() => {});
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
